import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Group } from "@/types";
import { Users, Shield } from "lucide-react";
import { useGroups } from "@/store/group";
import { Button } from "@/components/ui/Button";
import { useLayout } from "@/contexts/layout-context";
import { Loader } from "@/components/loader";
import {
  ComposedTable,
  StatusCell,
  ActionCell,
  type FilterDef,
  type FilterValues,
} from "@/components/table";
import { PageLayout } from "@/components/page-layout";


const FILTERS: FilterDef[] = [
  {
    type: "radio",
    key: "membership",
    label: "Membership",
    options: ["Any", "Small (0-50)", "Large (50+)"],
    defaultValue: "Any",
  },
  {
    type: "radio",
    key: "period",
    label: "Created Date",
    options: ["All Time", "2024 Only", "2023 Only"],
    defaultValue: "All Time",
  },
  {
    type: "radio",
    key: "system",
    label: "System Groups",
    options: ["Hide", "Show"],
    defaultValue: "Hide",
  },
];

export const GroupsPage = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const listGroups = useGroups((state) => state.list);
  const navigate = useNavigate();
  const { setBreadcrumbs } = useLayout();

  useEffect(() => {
    setBreadcrumbs([{ name: "Groups" }]);
    listGroups()
      .then(setGroups)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [listGroups, setBreadcrumbs]);

  const columns = [
    {
      id: "name",
      header: "Group Name",
      accessorKey: "name",
      cell: ({ row }: any) => {
        const g = row.original as Group;
        return (
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 border border-slate-100 dark:border-slate-800 shadow-sm bg-slate-50 dark:bg-slate-800 text-orange-500">
              <Shield className="w-4 h-4" />
            </div>
            <div className="flex flex-col">
              <span
                className="text-[14px] font-bold text-slate-900 dark:text-white cursor-pointer hover:text-[--primary]"
                onClick={() => navigate(`/groups/${g.id}/manage`)}
              >
                {g.name}
              </span>
              <span className="text-xs text-slate-500 mt-0.5">
                {g.description || "Enterprise role permissions"}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      cell: () => (
        <StatusCell status={"Active"} />
      ),
    },
    {
      id: "members",
      header: "Members",
      cell: () => (
        <span className="text-[13px] font-bold text-slate-600 dark:text-slate-400">
          {""}
        </span>
      ),
    },
    {
      id: "created_at",
      header: "Created Date",
      cell: ({ row }: any) => (
        <span className="text-[13px] text-slate-500 dark:text-slate-400">
          {row.original.created_at ? new Date(row.original.created_at).toLocaleDateString() : ""}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      meta: { align: "right" as const },
      cell: ({ row }: any) => (
        <ActionCell
          onClick={() => navigate(`/groups/${row.original.id}/manage`)}
        />
      ),
    },
  ];

  const filterFn = (row: Group, values: FilterValues) => {
    console.log("Filtering row", row, "with values", values);
    // Mock membership filter removed
    const period = values["period"] as string;
    if (period && period !== "All Time") {
      const year = new Date(row.created_at || Date.now()).getFullYear();
      if (period === "2024 Only" && year !== 2024) return false;
      if (period === "2023 Only" && year !== 2023) return false;
    }
    const system = values["system"] as string;
    if ((!system || system === "Hide") && row.is_system_managed) {
      return false;
    }
    return true;
  };

  const emptyState = (
    <div className="p-12 text-center flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
      <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
        <Users className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
        No groups found
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xs">
        Start by creating your first group to manage logical user collections.
      </p>
      <Button
        className="mt-6 bg-[#1e293b] hover:bg-slate-800 text-white rounded-full px-6"
        onClick={() => navigate("/m/groups/create")}
      >
        Create first group
      </Button>
    </div>
  );

  return (
    <PageLayout title="Groups" description="Manage logical user groups and access mappings.">
      {loading ? (
        <div className="flex justify-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
          <Loader />
        </div>
      ) : (
        <ComposedTable
          data={groups}
          columns={columns}
          searchPlaceholder="Search groups by name or description..."
          statusOptions={["All", "Active", "Inactive"]}
          statusField={() => "Active"}
          filters={FILTERS}
          filterFn={filterFn} 
          onAdd={() => navigate("/m/groups/create")}
          emptyState={emptyState}
          itemLabel="groups"
        />
      )}
    </PageLayout>
  );
};
